import { useMemo } from 'react';
import { stringUtil } from 'utils/index';

import endpoints from '../../services/api/endpoints';
import type {
  CategoriesResponse,
  CategoryResponse,
} from '../../services/api/types';
import useFetch from '../../services/api/useFetch';
import type { AddUpdateCategoryRequest } from '../../stores/product-store/types';
import type { CategoryItem } from '../../types/shared';

export const useOrganizationCategoryApi = () => {
  const api = useFetch();
  return useMemo(() => {
    return {
      async get(id: string): Promise<CategoryResponse> {
        try {
          const data: CategoryItem = await api.get(
            stringUtil.replaceAll(endpoints.categories.organization.base, {
              ':id': id,
            })
          );
          return { success: true, category: data };
        } catch (error: any) {
          return Promise.reject({ success: false, ...error });
        }
      },

      async list(vendorId: string): Promise<CategoriesResponse> {
        try {
          const data: CategoryItem[] = await api.get(
            endpoints.categories.search,
            { vendorId }
          );
          return { success: true, categories: data };
        } catch (error: any) {
          return Promise.reject({ success: false, ...error });
        }
      },

      async getCategories(organizationId: string): Promise<CategoriesResponse> {
        try {
          const data: CategoryItem[] = await api.get(
            endpoints.categories.organization.search,
            {
              organizationId,
              includeHidden: true,
            }
          );
          return { success: true, categories: data };
        } catch (error: any) {
          return Promise.reject({ success: false, ...error });
        }
      },

      async getVendorCategories(vendorId: string): Promise<CategoriesResponse> {
        try {
          const data: CategoryItem[] = await api.get(
            stringUtil.replaceAll(endpoints.categories.organization.vendor, {
              ':vendorId': vendorId,
            }),
            {
              includeHidden: true,
            }
          );
          return { success: true, categories: data };
        } catch (error: any) {
          return Promise.reject({ success: false, ...error });
        }
      },

      async add(category: AddUpdateCategoryRequest) {
        try {
          const data: CategoryItem = await api.post(
            endpoints.categories.organization.search,
            category
          );
          return { success: true, ...data };
        } catch (error: any) {
          return Promise.reject({ success: false, ...error });
        }
      },

      async update({ id = '', ...category }: AddUpdateCategoryRequest) {
        const data: CategoryItem = await api.put(
          stringUtil.replaceAll(endpoints.categories.organization.base, {
            ':id': id,
          }),
          { ...category, id }
        );
        return { success: true, ...data };
      },

      async delete(categoryId: string) {
        try {
          const data: CategoryResponse = await api.delete(
            stringUtil.replaceAll(endpoints.categories.organization.base, {
              ':id': categoryId,
            })
          );
          return { success: true, ...data };
        } catch (error: any) {
          return Promise.reject({ success: false, ...error });
        }
      },

      async toggle(categoryId: string, visible: boolean) {
        try {
          const data: CategoryResponse = await api.patch(
            stringUtil.replaceAll(endpoints.categories.organization.toggle, {
              ':id': categoryId,
              ':visible': String(visible),
            })
          );
          return { success: true, ...data };
        } catch (error: any) {
          return Promise.reject({ success: false, ...error });
        }
      },
    };
  }, [api]);
};
